import apiClient from './api';
import { User, LoginCredentials, RegisterData } from '../types/auth';

// Export new services
export { default as portfolioService } from './portfolioService';
export { default as alertService } from './alertService';
export { default as newsService } from './newsService';

// Authentication service
export class AuthService {
  static async login(credentials: LoginCredentials) {
    try {
      return await apiClient.login(credentials.identifier, credentials.password);
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }

  static async register(userData: RegisterData) {
    try {
      return await apiClient.register(userData);
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }

  static async logout() {
    try {
      return await apiClient.logout();
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }

  static async changePassword(oldPassword: string, newPassword: string) {
    try {
      return await apiClient.changePassword(oldPassword, newPassword);
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }

  static async exportAccount() {
    try {
      return await apiClient.exportAccount();
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }

  static async deleteAccount(password: string) {
    try {
      return await apiClient.deleteAccount(password);
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }

  static async refreshToken(refreshToken: string) {
    try {
      return await apiClient.refreshToken(refreshToken);
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }

  static isAuthenticated(): boolean {
    return apiClient.isAuthenticated();
  }

  static getCurrentUser(): User | null {
    return apiClient.getStoredUser() as User | null;
  }

  static async getProfile() {
    try {
      return await apiClient.getCurrentUser();
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }
}

// User service
export class UserService {
  static async getProfile() {
    try {
      return await apiClient.getCurrentUser();
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }

  static async updateProfile(userData: Partial<{
    first_name: string;
    last_name: string;
    email: string;
  }>) {
    try {
      return await apiClient.updateProfile(userData);
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }
}

// Financial data service
export class FinancialDataService {
  static async getInstrumentsByType(type: string) {
    try {
      // Backend endpoint: /financial/instruments/by_type/?type=CURRENCY
      const resp = await apiClient.get(`/financial/instruments/by_type/?type=${encodeURIComponent(type)}`);
      return resp.data;
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }

  static async getRealtimeLatest() {
    try {
      // Backend endpoint: /financial/realtime/latest/
      const resp = await apiClient.get(`/financial/realtime/latest/`);
      return resp.data;
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }

  static async getStocksList() {
    try {
      // Backend endpoint: /financial/instruments/by_type/?type=STOCK
      const resp = await apiClient.get(`/financial/instruments/by_type/?type=STOCK`);
      return resp.data;
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }

  static async getInstrumentDetail(symbol: string) {
    try {
      // Backend endpoint: /financial/instruments/{symbol}/
      const resp = await apiClient.get(`/financial/instruments/${encodeURIComponent(symbol)}/`);
      return resp.data;
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }

  static async getInstrumentRealtime(symbol: string) {
    try {
      // Backend endpoint: /financial/realtime/by_instrument/?symbol=AAPL
      const resp = await apiClient.get(`/financial/realtime/by_instrument/?symbol=${encodeURIComponent(symbol)}`);
      return resp.data;
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }

  static async getInstrumentHistorical(symbol: string, period: string = '1D') {
    try {
      // Backend endpoint: /financial/historical/by_instrument/?symbol=AAPL&period=1D
      const resp = await apiClient.get(`/financial/historical/by_instrument/?symbol=${encodeURIComponent(symbol)}&period=${period}`);
      return resp.data;
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }

  static async getLatestHistoricalData(symbol: string) {
    try {
      // Backend endpoint: /financial/historical/latest/?symbol=AAPL
      const resp = await apiClient.get(`/financial/historical/latest/?symbol=${encodeURIComponent(symbol)}`);
      return resp.data;
    } catch (error) {
      throw apiClient.handleError(error);
    }
  }
}

